import { faPencilAlt, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import { useLayoutEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { ROLE_EDITOR } from "../../../common/constant";
import { showDialog } from "../../../ducks/confirmDialog";
import StandardView from "../../common/StandardView";
import Table from "../../common/Table";
import Visibility from "../../common/VisibleContainer";
import CreateEditRoleDialog from "./CreateEditRoleDialog";

const RoleManagement = ({
  loadRoles,
  loadPermissions,
  roleList,
  permissionList,
  editedRole,
  editRole,
  saveRole,
  deleteRole,
  propertyChange,
  locale
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  useLayoutEffect(() => {
    loadRoles();
    loadPermissions();
  }, [loadRoles, loadPermissions]);

  return (
    <Visibility roles={[ROLE_EDITOR]}>
      <StandardView
        id="roleManagement"
        titleKey="role.management.page.title"
        actions={[
          {
            labelKey: "role.management.new.role",
            onClick: () => editRole({ permissions: [] }),
            icon: <FontAwesomeIcon icon={faPlus} className="me-2" />
          }
        ]}
      >
        <Table
          striped
          hover
          columns={[
            {
              name: "name",
              labelKey: "role.management.table.column.name"
            },
            {
              name: "title",
              labelKey: "role.management.table.column.title",
              customBodyRenderer: (role) => {
                let name = role.name;
                if (role.titleDe || role.titleEn) {
                  name = locale === "de" ? role.titleDe : role.titleEn;
                }
                return name;
              }
            },
            {
              name: "permissions",
              labelKey: "role.management.table.column.permissions",
              customBodyRenderer: (role) => {
                return role.permissions
                  ? role.permissions
                      .map((permission) => intl.formatMessage({ id: "user.management.roles." + permission.name }))
                      .join(" ")
                  : "";
              }
            },
            {
              name: "scope",
              labelKey: "role.management.table.column.scope",
              customBodyRenderer: (role) => {
                return role.scope ? intl.formatMessage({ id: "role.management.role.scope." + role.scope }) : "";
              }
            },
            {
              name: "management",
              labelKey: "app.common.title.actions",
              className: "actions",
              actions: [
                {
                  code: "edit",
                  labelKey: "app.common.action.edit",
                  icon: faPencilAlt,
                  onClick: (role) => editRole(role)
                  //disabled : (role) => role.id < 20
                },
                {
                  code: "delete",
                  labelKey: "app.common.action.delete",
                  icon: faTrash,
                  className: (role) => role.id < 20 ? "text-secondary" : "text-danger",
                  onClick: (role) => {
                    dispatch(
                      showDialog(
                        role.id,
                        () => deleteRole(role.id),
                        "",
                        "role.management.confirm.delete.role",
                        "confirmDialog.confirmLabel",
                        "confirmDialog.dismissLabel"
                      )
                    );
                  },
                  disabled: (role) => role.id < 20
                }
              ]
            }
          ]}
          data={roleList}
        />
        {editedRole && (
          <CreateEditRoleDialog
            role={editedRole}
            propertyChange={propertyChange}
            onCancel={() => editRole(undefined)}
            saveRole={saveRole}
            permissionList={permissionList}
            roleList={roleList}
          />
        )}
      </StandardView>
    </Visibility>
  );
};

RoleManagement.propTypes = {
  permissionList: PropTypes.arrayOf(PropTypes.object),
  roleList: PropTypes.arrayOf(PropTypes.object),
  editedRole: PropTypes.object,
  editRole: PropTypes.func.isRequired,
  saveRole: PropTypes.func.isRequired,
  deleteRole: PropTypes.func.isRequired,
  locale: PropTypes.string,
  propertyChange: PropTypes.func.isRequired,
  programChange: PropTypes.func,
  programInstanceChange: PropTypes.func,
  projectChange: PropTypes.func,
  loadRoles: PropTypes.func.isRequired,
  loadPermissions: PropTypes.func.isRequired,
  programDialogOpenChange: PropTypes.func,
  programInstanceDialogOpenChange: PropTypes.func,
  projectDialogOpenChange: PropTypes.func
};

export default RoleManagement;
